
import {
  CreateBookingV4,
  ErrorBookingV4,
  CommodityBookingV4,
  BookingTabV4,
  PaymentMethodV4
} from "@/domain/entities/BookingV4";
import { ClientNamePOSData } from "@/domain/entities/Partner";
import { OptionsClass } from "@/domain/entities/MainApp";
import {
  CustomerBookingV3,
  FraudStatus,
  ConfigValidationForm
} from "@/domain/entities/CreateBookingV3";
import { Options, prop, Vue } from "vue-class-component";
import Checkbox from "primevue/checkbox";
import { BookingController } from "@/app/ui/controllers/BookingController";
import debounce from "lodash/debounce";
import {
  senderCountrySchema,
  senderNameSchema,
  senderPhoneSchema,
  senderReceiverAddressSchema
} from "./validation";
import {
  parseMultiFieldZodError,
  parseZodError,
  validationCurrentIndex
} from "../../../create-booking-v3/utils";
import { AccountController } from "@/app/ui/controllers/AccountController";
import { LocationController } from "@/app/ui/controllers/LocationController";
import { CommodityController } from "@/app/ui/controllers/CommodityController";
import { RequestListCommodity } from "@/data/payload/api/CommodityApiRequest";
import { FlagsMedusa } from "@/feature-flags/flags-misc-medusa";
import { removeNewLineWhitespace } from "@/app/infrastructures/misc/Utils";
import {
  CustomerListEntities,
  ResponsePhoneChecker
} from "@/domain/entities/Booking";
import { FlagsPermissionBooking } from "@/feature-flags/flag-booking";
import { shouldSkipPhoneValidation } from "../../../booking-utils";
import { SttAdjustmentRule } from "@/domain/entities/ShipmentBooking";
import { defineAsyncComponent, PropType } from "vue";
import { CountryData } from "@/domain/entities/Location";

const FieldKlienV4 = defineAsyncComponent({
  loader: () => import("../field-klien/index.vue")
});

type CustomerParams = {
  search: string;
  isSender?: boolean;
};

class Props {
  modelValue = prop<CreateBookingV4>({
    default: new CreateBookingV4(),
    type: Object
  });
  errors = prop<ErrorBookingV4>({
    default: new ErrorBookingV4(),
    type: Object
  });
  currentIndex = prop<number>({
    default: -1,
    type: Number
  });
  senderCountryIndex = prop<number>({
    default: 1,
    type: Number
  });
  senderNameIndex = prop<number>({
    default: 2,
    type: Number
  });
  senderPhoneIndex = prop<number>({
    default: 3,
    type: Number
  });
  senderAddressIndex = prop<number>({
    default: 4,
    type: Number
  });
  activeTab = prop<typeof BookingTabV4[keyof typeof BookingTabV4]>({
    default: BookingTabV4.DOMESTIC,
    type: String as PropType<typeof BookingTabV4[keyof typeof BookingTabV4]>
  });
  clientIndex = prop<number>({ default: 2, type: Number });
  showClientField = prop<boolean>({ default: false, type: Boolean });
  selectedClient = prop<ClientNamePOSData | null>({
    default: null,
    type: Object as PropType<ClientNamePOSData | null>
  });
  clientErrorMessage = prop<string>({ default: "" });
  clients = prop<ClientNamePOSData[]>({ default: () => [] });
  clientOrigins = prop<OptionsClass[]>({ default: () => [] });
  selectedClientOrigin = prop<OptionsClass | null>({
    default: null,
    type: Object as PropType<OptionsClass | null>
  });
  selectedClientServiceTypes = prop<OptionsClass[]>({ default: () => [] });
  clientSearchKeyword = prop<string>({ default: "" });
  isClientInitialLoading = prop<boolean>({ default: false });
  isClientLoadingMore = prop<boolean>({ default: false });
  clientHasNextPage = prop<boolean>({ default: false });
  clientListErrorMessage = prop<string>({ default: "" });
  isClientOriginLoading = prop<boolean>({ default: false });
  clientOriginsHasNextPage = prop<boolean>({ default: false });
  configDistrict = prop<SttAdjustmentRule>({
    default: new SttAdjustmentRule(),
    type: Object
  });
  configName = prop<SttAdjustmentRule>({
    default: new SttAdjustmentRule(),
    type: Object
  });
  configPhone = prop<SttAdjustmentRule>({
    default: new SttAdjustmentRule(),
    type: Object
  });
  configAddress = prop<SttAdjustmentRule>({
    default: new SttAdjustmentRule(),
    type: Object
  });
  isClientDisabled = prop<boolean>({ default: false });
  isEdit = prop<boolean>({ default: false, type: Boolean });
  isClient = prop<boolean>({ default: false, type: Boolean });
}

@Options({
  name: "SenderSectionV4",
  components: {
    Checkbox,
    FieldKlienV4
  },
  emits: [
    "update:modelValue",
    "update:errors",
    "update:currentIndex",
    "autofillCommodity",
    "validationChange",
    "client-blur",
    "client-clear",
    "client-close-list",
    "client-focus",
    "client-load-more",
    "client-open-list",
    "client-retry",
    "client-select",
    "client-tab",
    "client-origin-focus",
    "client-origin-filter",
    "client-origin-scrolled",
    "update:client-origin",
    "update:client-search",
    "update:client-service-types",
    "selectPhoneNumber"
  ],
  watch: {
    "modelValue.sender": {
      deep: true,
      handler() {
        this.emitValidationChange();
      }
    },
    "modelValue.sender.phone": {
      handler() {
        this.onValidatePhone();
      }
    },
    isInternational: {
      deep: true,
      handler() {
        this.getCountryList();
      }
    },
    errors: {
      deep: true,
      handler() {
        this.emitValidationChange();
      }
    },
    "modelValue.pageMeta.activeTab": {
      deep: true,
      handler() {
        BookingController.setPhoneCheckerData(new ResponsePhoneChecker());
      }
    },
    "modelValue.sender.originCountryCode": {
      handler() {
        this.onUpdateOriginCountryDetail();
      }
    }
  }
})
class SenderSectionV4 extends Vue.with(Props) {
  async mounted() {
    if (
      !this.modelValue.sender.phoneCode ||
      (!this.isEdit && this.modelValue.sender.phoneCode === "+62")
    ) {
      this.modelValue.sender.phoneCode = !this.accountIsForeign ? "+62" : "+60";
      this.modelValue.sender.phoneFlag = !this.accountIsForeign
        ? "indonesia"
        : "malaysia";
    }

    this.getCountryList();

    BookingController.setPhoneCheckerData(new ResponsePhoneChecker());
    this.getCustomerList();
    this.emitValidationChange();
  }

  unmounted() {
    BookingController.setPhoneCheckerData(new ResponsePhoneChecker());
  }

  get configValidationForm() {
    return ConfigValidationForm;
  }

  get disabledName(): boolean {
    return this.configName.rule.includes(ConfigValidationForm.NON_EDITABLE);
  }

  get disabledPhone(): boolean {
    return this.configPhone.rule.includes(ConfigValidationForm.NON_EDITABLE);
  }

  get disabledAddress(): boolean {
    return this.configAddress.rule.includes(ConfigValidationForm.NON_EDITABLE);
  }

  get accountIsForeign(): boolean {
    return AccountController.accountData.accountIsForeign;
  }

  get districtDetail() {
    return LocationController.districtDetail;
  }

  get isInternational(): boolean {
    return this.activeTab === BookingTabV4.INTERNATIONAL;
  }

  // negara tujuan (international only)
  // TODO(follow-up ticket): `countries` (BulkData.ts) is a phone-dial-code list
  // ("+62", not "ID"), reused here for lack of a wired-in ISO country picker.
  // `originCountryCode` is read as an ISO code elsewhere (see CargoV2.ts).
  // Swap to LocationController.getCountryList()/CountryData once that search+
  // pagination UX is available for this field.
  get selectedCountry(): CountryData | null {
    return (
      this.countryList.find(
        (item) => item.code === this.modelValue.sender.originCountryCode
      ) || null
    );
  }
  set selectedCountry(value: CountryData | null) {
    this.modelValue.sender.originCountryCode = value?.code || "";
    this.onValidateCountry();
    this.emitModelValue();
  }
  onChangeOriginCountry(value: string | CountryData) {
    this.selectedCountry = value as CountryData;
    this.modelValue.sender.phoneCode = this.selectedCountry?.phoneCode || "";

    this.$emit("client-clear");
  }
  get errorMessageCountry(): string {
    if (!this.isInternational) return "";
    return this.errors.originCountry;
  }
  onValidateCountry() {
    if (!this.isInternational) {
      this.errors.originCountry = "";
      this.emitErrors();
      return "";
    }

    const message = parseZodError(
      senderCountrySchema,
      this.modelValue.sender.originCountryCode
    );
    this.errors.originCountry = message;
    this.emitErrors();

    return message;
  }

  // sender name
  get loadingCustomerList(): boolean {
    return BookingController.isLoadingCustomer;
  }
  get customerList() {
    return BookingController.senderCustomerList.data;
  }
  get customerRef() {
    return {
      refType:
        !AccountController.accountData.isPosAccount &&
        this.isClient &&
        (this.modelValue.sender.clientId ||
          AccountController.accountData.account_type_detail.id)
          ? "client"
          : "",
      refId: AccountController.accountData.isPosAccount
        ? 0
        : this.modelValue.sender.clientId ||
          AccountController.accountData.account_type_detail.id ||
          0
    };
  }

  fetchCustomer = debounce((search: string) => {
    this.getCustomerList({
      search,
      isSender: true
    });
  }, 1000);
  getCustomerList(params?: CustomerParams) {
    BookingController.getCustomerList({
      ...params,
      cache: true,
      search: params?.search || "",
      refType: this.customerRef.refType,
      refId: Number(this.customerRef.refId || 0)
    });
  }
  onChangeName(value: string) {
    const trimmed = value.trim();
    this.modelValue.sender.id = 0;
    this.modelValue.sender.name = trimmed;
    this.onValidateName();
    this.emitModelValue();

    if (!trimmed || !this.errorMessageName) {
      this.fetchCustomer(trimmed);
    }
  }
  onFocusCustomer(index: number, search: string) {
    this.onSelectCurrentIndex(index);

    this.getCustomerList({
      search,
      isSender: true
    });
  }
  onSelectCustomer(item: CustomerBookingV3) {
    if (!item) return false;

    if (item.customerCommodityCode) {
      void this.updateCommodity(item);
    } else {
      this.modelValue.commodity = new CommodityBookingV4();
    }

    this.modelValue.sender.id = item.customerId;
    this.modelValue.sender.name = item.customerName;
    this.modelValue.sender.phoneCode = item.customerPhoneCode;
    this.modelValue.sender.phone = item.customerPhoneNumber;
    this.modelValue.sender.address = item.customerAddress;
    this.modelValue.sender.phoneFlag = item.customerPhoneflag;

    this.onValidateName();
    const phoneError = this.onValidatePhone();
    this.onValidateAddress();
    this.emitModelValue();
    this.emitErrors();

    this.$emit("selectPhoneNumber", item.customerId);

    if (!phoneError) {
      this.onCheckFraudNumber(this.fullPhoneNumber);
    }
  }
  async updateCommodity(item: CustomerBookingV3) {
    try {
      const commodity = await CommodityController.getDetailCommodityByCode(
        item.customerCommodityCode
      );
      await CommodityController.getCommodityList(new RequestListCommodity());

      this.modelValue.commodity = new CommodityBookingV4({
        id: commodity.commodity_id,
        name: !this.accountIsForeign
          ? commodity.commodity_name
          : commodity.commodity_name_en || " ",
        code: commodity.commodity_code,
        isDangerousGoods: commodity.isDangerousGoods,
        isQuarantine: commodity.isQuarantine,
        status: commodity.commodity_status
      });

      this.$emit("autofillCommodity");
    } catch (error) {
      this.modelValue.commodity = new CommodityBookingV4({
        isNotFound: true
      });
    }
  }
  get errorMessageName(): string {
    if (validationCurrentIndex(this.senderNameIndex, this.currentIndex)) {
      return this.onValidateName();
    }

    return this.errors.sender;
  }
  onValidateName() {
    const message = parseZodError(
      senderNameSchema,
      this.modelValue.sender.name
    );
    this.errors.sender = message;
    this.emitErrors();

    return message;
  }

  // sender phone number
  isTooltipLimited = false;
  toggleTooltipLimited(event: any) {
    this.isTooltipLimited = !this.isTooltipLimited;

    const refs: any = this.$refs;
    refs?.["limited"]?.toggle(event);
  }

  get errorMessagePhone(): string {
    if (validationCurrentIndex(this.senderPhoneIndex, this.currentIndex)) {
      return this.onValidatePhone();
    }

    return this.errors.phoneSender;
  }
  onValidatePhone() {
    if (shouldSkipPhoneValidation(this.fullPhoneNumber, false)) {
      this.errors.phoneSender = "";
      this.emitErrors();
      return "";
    }

    const message = parseZodError(senderPhoneSchema, this.fullPhoneNumber);
    this.errors.phoneSender = message;
    this.emitErrors();

    return message;
  }
  get fullPhoneNumber() {
    const { phoneCode, phone } = this.modelValue.sender;
    return phone ? `${phoneCode}${phone}`.replace("+", "") : phone;
  }
  onChangePhone() {
    this.modelValue.sender.id = 0;
    const phoneError = this.onValidatePhone();
    this.emitModelValue();

    if (!phoneError) {
      this.fetchCustomer(this.fullPhoneNumber);
      this.onCheckFraudNumber(this.fullPhoneNumber);
    }
  }

  onCheckFraudNumber = debounce(async (phone: string) => {
    if (shouldSkipPhoneValidation(this.fullPhoneNumber, false)) {
      return;
    }

    BookingController.setPhoneCheckerData(new ResponsePhoneChecker());

    await BookingController.phoneChecker(phone);

    const isBlacklist = BookingController.phoneCheckerData.isBlacklist;

    if ((isBlacklist || this.isErrorFraudLimited) && !this.isEdit) {
      this.modelValue.paymentMethod.method = PaymentMethodV4.CASH;
    }
  }, 1000);
  get isErrorFraudLimited() {
    return (
      BookingController.phoneCheckerData.status === FraudStatus.LIMITED &&
      FlagsPermissionBooking.disable_cod_dfod_for_limited_number.isEnabled()
    );
  }
  get isErrorFraudSuspected() {
    return BookingController.phoneCheckerData.status === FraudStatus.SUSPECTED;
  }

  // sender address
  get errorMessageAddress(): string {
    if (
      validationCurrentIndex(this.senderAddressIndex, this.currentIndex) ||
      this.modelValue.sender.address
    ) {
      return this.onValidateAddress();
    }

    return this.errors.addressSender;
  }
  onValidateAddress() {
    const message = parseMultiFieldZodError(
      senderReceiverAddressSchema,
      {
        addressReceiver: this.modelValue.receiver.address,
        addressSender: this.modelValue.sender.address
      },
      "addressSender"
    );
    this.errors.addressSender = message;
    this.emitErrors();

    return message;
  }
  get maxAddressLength() {
    return FlagsMedusa.config_max_address.getValue();
  }
  replaceNewLine(value: string) {
    const removedNewLineAddress = removeNewLineWhitespace(value, " ");

    this.modelValue.sender.address = removedNewLineAddress;
    this.emitModelValue();

    return removedNewLineAddress;
  }

  onSelectCurrentIndex(index: number) {
    setTimeout(() => {
      if (!validationCurrentIndex(index, this.currentIndex)) {
        this.$emit("update:currentIndex", index);
      }
    }, 0);
  }
  async onScroll(search: string) {
    const nextPage = BookingController.senderCustomerList.pagination.page + 1;
    const data = await BookingController.getCustomerInfiniteScrollList({
      cache: true,
      search: search || "",
      refType: this.customerRef.refType,
      refId: Number(this.customerRef.refId || 0),
      isSender: true,
      page: nextPage
    });

    BookingController.setSenderCustomerList(
      new CustomerListEntities(data.pagination, [
        ...this.customerList,
        ...data.data
      ])
    );
  }

  // FormCompletion contract (GENESIS-19583 consumes validationChange)
  emitModelValue() {
    this.$emit("update:modelValue", this.modelValue);
  }
  emitErrors() {
    this.$emit("update:errors", this.errors);
  }
  get isSenderValid(): boolean {
    const { name, phone, address } = this.modelValue.sender;
    const validForm = [
      name,
      phone,
      address,
      !this.errors.sender,
      !this.errors.phoneSender,
      !this.errors.addressSender
    ].every(Boolean);

    if (this.isInternational) {
      return validForm;
    }

    return validForm && !this.errorMessageCountry;
  }
  emitValidationChange() {
    this.$emit("validationChange", this.isSenderValid);
  }

  get isInternalAccount() {
    return AccountController.accountData.isInternalAccount;
  }

  countryList = [] as CountryData[];
  get countryListFiltered() {
    return this.countryList.filter(
      (item) => item.code !== this.modelValue.receiver.destinationCountryCode
    );
  }

  async getCountryList() {
    const res = await LocationController.getCountryList({
      search: "",
      status: "active",
      page: 1,
      limit: 250
    });

    if (!res?.countryData) return;

    this.countryList = res.countryData.map((item) => ({
      ...item,
      label: `${item.name} (${item.code})`
    }));
  }

  onUpdateOriginCountryDetail() {
    if (!this.isInternational || !this.modelValue.sender?.originCountryCode) {
      return;
    }

    this.modelValue.sender.originCountry = this.countryList.find(
      (item) => item.code === this.modelValue.sender.originCountryCode
    ) as CountryData;
  }
}

export default SenderSectionV4;
